/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {ModelResponse, nullify, ResponseData} from '@dvrd/dvr-controls';
import {
    createEmailTemplate, deleteEmailTemplate, duplicateEmailTemplate, getEmailTemplate, getEmailTemplates,
    testEmailTemplate, updateEmailTemplate
} from "../util/requestSender";
import {deepClone} from "../util/utils";
import {UnlayerDesign} from "../util/interfaces";

interface Props extends BaseProps {
    account_id?: string;
    name?: string;
    sender?: string;
    reply_to?: string;
    bcc?: string;
    subject?: string;
    body?: string;
    html_body?: string;
    json?: UnlayerDesign | null;
    draft?: boolean;
    with_test_invoice?: boolean;
}

export interface EmailTemplateFields extends BaseFields {
    account_id: string;
    name: string;
    sender: string;
    reply_to: string;
    bcc: string;
    subject: string;
    body: string;
    html_body: string;
    json: UnlayerDesign | null;
    draft: boolean;
    with_test_invoice: boolean;
}

const fields: EmailTemplateFields = {
    id: '',
    created_at: '',
    updated_at: '',
    account_id: '',
    name: '',
    sender: '',
    reply_to: '',
    bcc: '',
    subject: '',
    body: '',
    html_body: '',
    json: null,
    draft: true,
    with_test_invoice: false,
};

const fieldsDefinition: { [key in keyof EmailTemplateFields]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    account_id: createFieldDef(ModelFieldType.STRING),
    name: createFieldDef(ModelFieldType.STRING),
    sender: createFieldDef(ModelFieldType.STRING),
    reply_to: createFieldDef(ModelFieldType.STRING),
    bcc: createFieldDef(ModelFieldType.STRING),
    subject: createFieldDef(ModelFieldType.STRING),
    body: createFieldDef(ModelFieldType.STRING),
    draft: createFieldDef(ModelFieldType.BOOLEAN),
    with_test_invoice: createFieldDef(ModelFieldType.BOOLEAN),
    json: createFieldDef(ModelFieldType.OBJECT, null, true),
    html_body: createFieldDef(ModelFieldType.STRING),
};

const ignoreProps: Array<keyof Props> = [];

export default class EmailTemplate extends BaseModel<Props, EmailTemplateFields> {

    get id(): string {
        return this.fields.id as string;
    }

    get account_id(): string {
        return this.fields.account_id;
    }

    set account_id(id: string) {
        this.fields.account_id = id;
    }

    get name(): string {
        return this.fields.name;
    }

    set name(name: string) {
        this.fields.name = name;
    }

    get sender(): string {
        return this.fields.sender;
    }

    set sender(name: string) {
        this.fields.sender = name;
    }

    get replyTo(): string {
        return this.fields.reply_to;
    }

    set replyTo(reply_to: string) {
        this.fields.reply_to = reply_to;
    }

    get bcc(): string {
        return this.fields.bcc;
    }

    set bcc(bcc: string) {
        this.fields.bcc = bcc;
    }

    get subject(): string {
        return this.fields.subject;
    }

    set subject(subject: string) {
        this.fields.subject = subject;
    }

    get body(): string {
        return this.fields.body;
    }

    set body(body: string) {
        this.fields.body = body;
    }

    get draft(): boolean {
        return this.fields.draft;
    }

    get withTestInvoice(): boolean {
        return this.fields.with_test_invoice;
    }

    set withTestInvoice(withTestInvoice: boolean) {
        this.fields.with_test_invoice = withTestInvoice;
    }

    get htmlBody(): string {
        return this.fields.html_body;
    }

    set htmlBody(body: string) {
        this.fields.html_body = body;
    }

    get json(): UnlayerDesign | null {
        return this.fields.json;
    }

    set json(json: UnlayerDesign | null) {
        this.fields.json = json;
    }

    hasJson = (): boolean => {
        return (this.json !== null && Object.keys(this.json).length > 0) || !this.body.length;
    }

    hasJsonContent = (): boolean => {
        return this.json !== null && Object.keys(this.json).length > 0;
    }

    test = (email: string, emailStepId: string | null, callback: ModelResponse<EmailTemplate>) => {
        testEmailTemplate({
            data: {
                account_id: this.account_id,
                email,
                reply_to: nullify(this.replyTo),
                subject: this.subject,
                body: this.body,
                sender_name: nullify(this.sender),
                html_body: this.htmlBody,
                email_step_id: emailStepId
            },
            callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        })
    }

    update = (callback: ModelResponse<EmailTemplate>) => {
        updateEmailTemplate({
            data: {
                account_id: this.account_id,
                template_id: this.id,
                name: this.name,
                sender: this.sender,
                reply_to: nullify(this.replyTo),
                bcc: nullify(this.bcc),
                subject: this.subject,
                body: this.body,
                with_test_invoice: this.withTestInvoice,
                mail_json: this.json,
                html_body: this.htmlBody,
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.template);
                callback(this, data.success, data);
            }
        });
    }

    save = (callback: ModelResponse<EmailTemplate>) => {
        createEmailTemplate({
            data: {
                account_id: this.account_id,
                name: this.name,
                subject: this.subject,
                body: this.body,
                with_test_invoice: this.withTestInvoice,
                mail_json: this.json,
                html_body: this.htmlBody,
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.template)
                callback(this, data.success, data);
            }
        });
    };

    delete = (callback: ModelResponse<EmailTemplate>) => {
        deleteEmailTemplate({
            id: this.id, account_id: this.account_id, callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        })
    }

    duplicate = (callback: ModelResponse<EmailTemplate | null>) => {
        duplicateEmailTemplate({
            id: this.id, account_id: this.account_id, callback: (data: ResponseData) => {
                const template = data.success ? new EmailTemplate(data.template) : null;
                callback(template, data.success, data);
            }
        });
    };

    static get = (account_id: string, template_id: string, callback: ModelResponse<EmailTemplate>) => {
        getEmailTemplate({
            data: {
                account_id,
                template_id
            },
            callback: (data: ResponseData) => {
                const {success} = data,
                    template = new EmailTemplate(data.template);
                callback(template, success, data);
            }
        });
    }

    static getAll = (account_id: string, limit: number = 10, page: number = -1,
                     callback: ModelResponse<EmailTemplate[]>) => {
        getEmailTemplates({
            data: {limit, page, account_id}, callback: (data: ResponseData) => {
                const {success} = data,
                    templates: EmailTemplate[] = success ? data.templates.map((inv: any) => new EmailTemplate(inv)) : [];
                callback(templates, success, data);
            }
        })
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    protected build = (props?: Props) => {
        this.constructFields(props);
    }

    clone = (deep: boolean = true): EmailTemplate => {
        let fields = this.fields;
        if(deep) fields = deepClone(fields);
        return new EmailTemplate(fields);
    }
}
